/**
 * Обозначения ударения (спек 2026-10-15-stress-glossary-design.md, «Правила разбора текста записей»).
 * Без зависимостей от Node: модуль импортирует и клиент (часть 1б).
 * Каноническая форма — U+0301 отдельным знаком сразу после ударной гласной, остальное NFC; у «ё» знака нет.
 */
export const ACUTE = '́';
/** Латиница ударяется так же, как кириллица (решение пользователя 2026-10-16, глоссарий «Ведьмака») */
const VOWELS = 'аеёиоуыэюяaeiouy';

export function isVowel(ch: string): boolean {
  return VOWELS.includes(ch.toLowerCase());
}

/** Есть латинская буква — слово читается как латиница (токенизатор, числа «в книге») */
export const hasLatin = (text: string): boolean => /[a-z]/i.test(text);

/** Латинская буква, у которой последний знак — акут: «ó», «Á», «ǘ» (ü + акут) */
const LATIN_ACUTE = /^([a-z]\p{M}*)\u0301$/iu;

/**
 * Каноническая форма: NFC, но акут над латинской буквой — отдельным знаком. NFC склеил бы «o» + U+0301 в «ó» — знак
 * перестал бы быть отдельным символом, и позиции ударения потерялись бы. Кириллицу NFC с акутом не склеивает.
 */
export function canonical(text: string): string {
  return text.normalize('NFC').replace(/[^\p{ASCII}]/gu, (ch) => {
    const m = LATIN_ACUTE.exec(ch.normalize('NFD'));
    return m ? m[1].normalize('NFC') + ACUTE : ch;
  });
}

export function stripStress(text: string): string {
  return canonical(text).split(ACUTE).join('');
}

/**
 * Слово записи и формы в базе — строчными (решение пользователя 2026-10-16): на экране слова и так строчные,
 * а заглавная в начале фразы («ПрИнял») не попадает в словарь. Знаки остаются на своих буквах.
 */
export function storedText(text: string): string {
  return canonical(text).toLowerCase();
}

/**
 * Знаки над латинскими буквами (é, ç, ñ) слово не различают: «Élan» в книге — то же, что «Elan» в глоссарии
 * (решение пользователя 2026-10-16). Только после латинской буквы: «й» (и + бреве) — своя буква, кириллицу не трогаем.
 */
function foldLatinMarks(text: string): string {
  return text.normalize('NFD').replace(/([a-z])[\u0300-\u036f]+/g, '$1').normalize('NFC');
}

/** Ключ записи и слова книги: регистр, «ё»→«е», без знака, знаки над латиницей сняты, пробелы схлопнуты. */
export function normalizeKey(text: string): string {
  return foldLatinMarks(stripStress(text).toLowerCase().replace(/ё/g, 'е')).replace(/\s+/g, ' ').trim();
}

export function vowelCount(word: string): number {
  return [...word].filter(isVowel).length;
}

const isYo = (ch: string) => ch === 'ё' || ch === 'Ё';

/** Слово со знаками: индексы — позиции ударных гласных в слове без знаков. */
export function stressedFrom(word: string, stress: readonly number[]): string {
  const marks = new Set(stress);
  let out = '';
  [...word].forEach((ch, i) => {
    out += ch;
    if (marks.has(i) && !isYo(ch)) out += ACUTE;
  });
  return canonical(out);
}

/** Позиции знаков в слове без знаков. «ё» сюда не попадает: её ударение подразумевается. */
export function stressIndexes(stressed: string): number[] {
  const out: number[] = [];
  let i = 0;
  for (const ch of canonical(stressed)) {
    if (ch === ACUTE) out.push(i - 1);
    else i++;
  }
  return out;
}

/** Позиции «ё» в слове без знаков: в книге на их месте бывает «е» — просмотрщик дорисует точки. */
export function yoIndexes(text: string): number[] {
  return [...stripStress(text)].flatMap((ch, i) => (isYo(ch) ? [i] : []));
}

/**
 * Вариант формы — та же форма со знаком ударения: регистр не важен, «е» ↔ «ё» можно поменять (выбор «ё» у склонения в меню
 * слова: «осведомленный» → «осведомлённый»), ударение на гласной или «ё».
 */
export function isFormVariant(form: string, variant: string): boolean {
  const word = stripStress(variant);
  return [...word].length === [...form].length && normalizeKey(word) === normalizeKey(form) && checkStress(word, stressIndexes(variant)).ok;
}

export type ParsedWord = { word: string; stress: number[]; status: 'marked' | 'auto' | 'none' };

type PartResult = { word: string; stress: number[]; status: ParsedWord['status'] };

function parsePart(raw: string): PartResult {
  if (raw.includes('+')) {
    const at = raw.indexOf('+');
    const word = raw.replace('+', '');
    return { word, stress: isVowel(word[at] ?? '') ? [at] : [], status: isVowel(word[at] ?? '') ? 'marked' : 'none' };
  }
  if (raw.includes(ACUTE)) {
    const word = stripStress(raw);
    const at = stressIndexes(raw)[0];
    return { word, stress: [at], status: 'marked' };
  }
  const chars = [...raw];
  const caps = chars.flatMap((c, i) => (/\p{Lu}/u.test(c) ? [i] : []));
  const initial = caps[0] === 0;
  let stress = caps.filter((i) => isVowel(chars[i]));
  // Первая заглавная — обычная, если в слове есть ещё заглавные; одна заглавная гласная в начале — это ударение.
  // У латиницы одна заглавная в начале — просто заглавная («Oui», «Encyclopedia»): ударение там ставят акутом («Áen»)
  if (initial && caps.length > 1) stress = stress.filter((i) => i !== 0);
  else if (initial && (!isVowel(chars[0]) || hasLatin(raw))) stress = [];
  const word = (initial ? chars[0] : chars[0].toLowerCase()) + chars.slice(1).join('').toLowerCase();
  if (stress.length > 0) return { word, stress: stress.slice(0, 1), status: 'marked' };
  // Без отметки: «ё» сама ударная, односложное — очевидно; явная отметка важнее «ё» («МьёльнИр»)
  return { word, stress: [], status: [...word].some(isYo) || vowelCount(word) <= 1 ? 'auto' : 'none' };
}

/** Одно слово в любом из обозначений: заглавная гласная, знак U+0301, «+» перед гласной. Части через дефис — по отдельности. */
export function parseWordNotation(raw: string): ParsedWord {
  const parts = canonical(raw).trim().split('-');
  let word = '';
  const stress: number[] = [];
  const statuses: ParsedWord['status'][] = [];
  parts.forEach((p, k) => {
    if (k > 0) word += '-';
    const r = parsePart(p);
    stress.push(...r.stress.map((i) => i + word.length));
    word += r.word;
    statuses.push(r.status);
  });
  // Размечена хоть одна часть — слово размечено, части без отметки остаются без знака («Ру́сти-Рыжик», решение 2026-10-16)
  const status = statuses.includes('marked') ? 'marked' : statuses.includes('none') ? 'none' : 'auto';
  return { word, stress, status };
}

export type StressCheck = { ok: true } | { ok: false; reason: 'not_single_word' | 'not_vowel' | 'two_in_part' | 'empty' };

/** Запись-ударение: одно слово; знаки только на гласных; не больше одного на часть через дефис; хотя бы один (или «ё»). */
export function checkStress(word: string, stress: readonly number[]): StressCheck {
  if (/\s/.test(word.trim())) return { ok: false, reason: 'not_single_word' };
  const chars = [...word];
  if (stress.some((i) => !isVowel(chars[i] ?? ''))) return { ok: false, reason: 'not_vowel' };
  let partStart = 0;
  for (const part of word.split('-')) {
    const inPart = stress.filter((i) => i >= partStart && i < partStart + part.length);
    if (inPart.length > 1) return { ok: false, reason: 'two_in_part' };
    partStart += part.length + 1;
  }
  if (stress.length === 0 && !chars.some(isYo)) return { ok: false, reason: 'empty' };
  return { ok: true };
}
